import { useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { LOCALE_COMMON_PATHS, Namespaces } from 'types/locales';
import { formatImageCount } from './format-image-count';

export interface UseImageCounterOptions {
  current: number;
  total: number;
  variant?: 'mobile' | 'desktop';
}

export interface UseImageCounterResult {
  /** Formatted display text */
  text: string;
  /** Aria label for accessibility */
  ariaLabel: string;
}

/**
 * Hook for image counter formatting and i18n
 */
export function useImageCounter({
  current,
  total,
  variant = 'mobile',
}: UseImageCounterOptions): UseImageCounterResult {
  const { t } = useTranslation(Namespaces.COMMON);

  return useMemo(() => {
    const text = variant === 'mobile'
      ? formatImageCount(current, total)
      : t(LOCALE_COMMON_PATHS.GALLERY_SEE_ALL_PHOTOS, {
          count: total,
          defaultValue: `See All ${total} Photos`,
        });

    const ariaLabel = t(LOCALE_COMMON_PATHS.GALLERY_IMAGE_COUNT_ARIA, {
      current,
      total,
      defaultValue: `Image ${current} of ${total}`,
    });

    return { text, ariaLabel };
  }, [current, total, variant, t]);
}
